import {createUseStyles} from 'react-jss'
import {Colors} from '../../constants/colors'
import {formatDateString} from '../../utils/dateHelper'
import {PayDeviationModel} from '../../redux/types'
import {formatMoney} from '../../utils/moneyHelper'
import Pagination from '../Pagination'
import React, {useMemo, useState} from 'react'
import {
  ColumnDef,
  ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import Card from '../Card'

interface Props {
  payDeviationsOnPage: PayDeviationModel[]
  pageIndex: number
  pageSize: number
  totalCount: number
  onPageChange: (pageIndex: number) => void
}

const notSpecified = 'Not specified'

const PayDeviationList = ({payDeviationsOnPage, pageIndex, pageSize, totalCount, onPageChange}: Props) => {
  const styles = useStyles()

  const [expanded, setExpanded] = useState<ExpandedState>({})

  const columns = useMemo<ColumnDef<PayDeviationModel>[]>(() => [
    {
      id: 'grossAmount',
      header: 'Gross amount',
      cell: ({row}) => (
        <span>
          {formatMoney(row.original.grossValue)}
        </span>
      ),
    },
    {
      id: 'date',
      header: 'Date',
      cell: ({row}) => <span>{formatDateString(row.original.date)}</span>,
    },
    {
      id: 'description',
      header: 'Description',
      cell: ({row}) => <span>{row.original.description}</span>,
    },
  ], [])

  const table = useReactTable({
    data: payDeviationsOnPage ?? [],
    columns,
    state: {expanded},
    onExpandedChange: setExpanded,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: () => true,
    manualPagination: true,
    pageCount: Math.ceil(totalCount / pageSize),
  })

  return (
    <div className={styles.container}>
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <th key={header.id} className={styles.headerCell}>
                  {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                </th>
              ))}
            </tr>
          ))}
          </thead>
          <tbody>
          {table.getRowModel().rows.map(row => (
            <React.Fragment key={row.id}>
              <tr
                className={styles.bodyRow}
                onClick={() => row.toggleExpanded()}
                tabIndex={0}
              >
                {row.getVisibleCells().map(cell => (
                  <td key={cell.id} className={styles.bodyCell}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
              {row.getIsExpanded() && (
                <tr>
                  <td className={styles.expandedCell} colSpan={Math.ceil(row.getVisibleCells().length)}>
                    <div className={styles.detailsContainer}>
                      <Card className={`${styles.detailsCard} card`}>
                        <h6>Contract</h6>
                        <p>External id: <span>{row.original.employmentContract.externalId || notSpecified}</span></p>
                        <p>Start: <span>{row.original.employmentContract.startDate || notSpecified}</span></p>
                        <p>End: <span>{row.original.employmentContract.endDate || notSpecified}</span></p>
                      </Card>
                      <Card className={`${styles.detailsCard} card`}>
                        <h6>Pay articles</h6>
                        {(row.original.payArticles?.length ?? 0) > 0 ? (
                          <div className={styles.payArticlesList}>
                            {row.original.payArticles.map((payArticle, index) => (
                              <div className={styles.payArticleItem} key={`${payArticle.code}-${index}`}>
                                <p>{payArticle.displayName || notSpecified} ({payArticle.code || notSpecified})</p>
                                <p>Quantity: <span>{payArticle.quantity}</span></p>
                                <p>Gross value: <span>{formatMoney(payArticle.grossValue)}</span></p>
                                <p>Payment date: <span>{payArticle.payday?.paymentDate ? formatDateString(payArticle.payday.paymentDate) : notSpecified}</span></p>
                                <p>Accrual type: <span>{payArticle.accrualType || notSpecified}</span></p>
                                <p>Approved: <span>{payArticle.approved ? 'yes' : 'no'}</span></p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p>No pay articles</p>
                        )}
                      </Card>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
          </tbody>
        </table>
      </div>

      <div className={styles.pagination}>
        <Pagination
          pageIndex={pageIndex}
          itemCount={totalCount}
          pageSize={pageSize}
          siblingCount={1}
          onPageChange={onPageChange}
        />
      </div>
    </div>
  )
}

export default PayDeviationList

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  tableContainer: {
    overflow: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: '0 0px',
    fontSize: '14px',
  },
  headerCell: {
    padding: '6px 12px',
    textAlign: 'left',
    fontFamily: 'Aestetico-Bold'
  },
  bodyRow: {
    cursor: 'pointer',
    transition: '0.4s',
    '&:hover': {
      backgroundColor: Colors.darkGrayHover,
    },
  },
  bodyCell: {
    padding: '2px 12px',
    textAlign: 'left',
    '&:first-child': {
      borderTopLeftRadius: '6px',
      borderBottomLeftRadius: '6px',
    },
    '&:last-child': {
      borderTopRightRadius: '6px',
      borderBottomRightRadius: '6px',
    },
  },
  expandedCell: {
    paddingTop: 2,
    paddingBottom: 22,
    paddingLeft: 22,
    paddingRight: 2,
  },
  detailsContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  detailsCard: {
    flex: 1,
    padding: 8,
    borderRadius: 8,
    backgroundColor: Colors.grayCard,
  },
  payArticlesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  payArticleItem: {
    paddingBottom: 8,
    borderBottom: `1px solid ${Colors.grayLine}`,
    '&:last-child': {
      borderBottom: 'none',
      paddingBottom: 0,
    },
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center'
  }
})
